import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Building2 } from 'lucide-react'
import { Section } from '@/components/layout/Section'
import { Container } from '@/components/layout/Container'
import { CardSlider } from '@/components/ui/CardSlider'
import { AnimateIn } from '@/components/motion/AnimateIn'
import { useCityStore } from '@/store/cityStore'
import { buildPropertyHref } from '@/lib/propertyNavLinks'
import type { Category } from '@/types/home'
import { OptimizedImage } from '@/components/ui/OptimizedImage'
import { cn } from '@/lib/utils'

const CARD_THEMES: { bg: string; title: string; sub: string; hover: string }[] = [
  { bg: 'bg-amber-50', title: 'text-amber-900', sub: 'text-amber-700', hover: 'hover:border-amber-200' },
  { bg: 'bg-sky-50', title: 'text-sky-900', sub: 'text-sky-700', hover: 'hover:border-sky-200' },
  { bg: 'bg-emerald-50', title: 'text-emerald-900', sub: 'text-emerald-700', hover: 'hover:border-emerald-200' },
  { bg: 'bg-violet-50', title: 'text-violet-900', sub: 'text-violet-700', hover: 'hover:border-violet-200' },
  { bg: 'bg-rose-50', title: 'text-rose-900', sub: 'text-rose-700', hover: 'hover:border-rose-200' },
  { bg: 'bg-teal-50', title: 'text-teal-900', sub: 'text-teal-700', hover: 'hover:border-teal-200' },
  { bg: 'bg-orange-50', title: 'text-orange-900', sub: 'text-orange-700', hover: 'hover:border-orange-200' },
  { bg: 'bg-indigo-50', title: 'text-indigo-900', sub: 'text-indigo-700', hover: 'hover:border-indigo-200' },
]

function CategoryCard({
  cat,
  index,
  cityName,
}: {
  cat: Category
  index: number
  cityName: string
}) {
  const [imgError, setImgError] = useState(false)
  const title = cat.displayName ?? cat.name
  const theme = CARD_THEMES[index % CARD_THEMES.length]
  const citySlug = cityName.toLowerCase()

  return (
    <Link
      to={buildPropertyHref('buy', {
        city: citySlug,
        category: cat.name,
        categorySlug: cat.slug,
      })}
      className={cn(
        'group block overflow-hidden rounded-2xl border border-neutral-100 transition-colors duration-200',
        theme.bg,
        theme.hover,
      )}
    >
      <div className="aspect-4/3 overflow-hidden bg-neutral-100 md:aspect-16/10">
        {!imgError && cat.image ? (
          <OptimizedImage
            src={cat.image}
            alt={title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
            priority={false}
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-4xl opacity-40">{cat.icon}</div>
        )}
      </div>
      <div className="px-3 py-3.5 text-center sm:px-4 sm:py-4">
        <h3 className={cn('font-display text-[15px] font-bold leading-snug sm:text-lg', theme.title)}>
          {title}
        </h3>
        <p className={cn('mt-1 text-[11px] font-semibold sm:text-sm', theme.sub)}>
          {cat.count.toLocaleString('en-IN')}+ Properties
        </p>
      </div>
    </Link>
  )
}

export function PropertyCategories({ categories }: { categories: Category[] }) {
  const { cityName } = useCityStore()

  return (
    <Container className="overflow-x-clip">
      <AnimateIn className="min-w-0">
        <Section
          title="Apartments, Villas and more"
          subtitle={`in ${cityName}`}
        >
          {categories.length > 0 ? (
            <CardSlider size="property" navVariant="side" hideScrollbar>
              {categories.map((cat, index) => (
                <CategoryCard key={cat.id} cat={cat} index={index} cityName={cityName} />
              ))}
            </CardSlider>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 py-16 text-center">
              <Building2 className="h-10 w-10 text-neutral-300" />
              <p className="mt-3 text-sm text-neutral-500">No categories available yet</p>
            </div>
          )}
        </Section>
      </AnimateIn>
    </Container>
  )
}
